import {defineStore} from 'pinia';
import {ref, watch} from 'vue';

const STORAGE_KEY_COLOR = 'preferred-color-scheme';
const STORAGE_KEY_FONT_SIZE = 'preferred-min-font-size';

/** Available color scheme options for the app theme */
export enum ColorScheme {
  dark = 'dark',
  light = 'light',
  system = 'system',
}

/** Minimum font size presets in pixels for accessibility */
export enum MinFontSize {
  normal = 16,
  large = 18,
  extraLarge = 20,
}

export const useUserPreferencesStore =
  /** Persists user display preferences (color scheme, font size) to localStorage */
  defineStore('user-preferences-store', () => {
  // default is adaptive, means we respect the current settings of the OS
  // TODO we could override the colorTheme to 'light' or 'dark' on a user-profile page. Simply set this state:
  const colorScheme = ref<ColorScheme>(localStorage.getItem(STORAGE_KEY_COLOR) as ColorScheme || ColorScheme.system);

  // Font size preference - synced with backend, localStorage as fallback
  const storedFontSize = localStorage.getItem(STORAGE_KEY_FONT_SIZE);
  const minFontSize = ref<number>(storedFontSize ? parseInt(storedFontSize, 10) : MinFontSize.normal);

  watch(
    colorScheme,
    (newScheme: ColorScheme): void => {
      if ([ColorScheme.light, ColorScheme.dark, ColorScheme.system].includes(newScheme)) {
        localStorage.setItem(STORAGE_KEY_COLOR, newScheme);
      }
    },
    {immediate: true},
  );

  watch(
    minFontSize,
    (newSize: number): void => {
      localStorage.setItem(STORAGE_KEY_FONT_SIZE, String(newSize));
    },
    {immediate: true},
  );

  function clearStore(): void {
    localStorage.removeItem(STORAGE_KEY_COLOR);
    localStorage.removeItem(STORAGE_KEY_FONT_SIZE);
    colorScheme.value = ColorScheme.system;
    minFontSize.value = MinFontSize.normal;
  }

  return {colorScheme, minFontSize, clearStore};
});


